
// LiveMap.tsx
import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps as waitGoogleMapsLoad } from '../../../common/loadGoogleMaps';
import { axiosInstance } from '@/services/axios.service';
import { X4bActivityDto, ActivityHelper } from '@/modules/vehicle/engine/X4bActivityDto';
import { ENDPOINTS } from '@/constants/endpoints';
import { createMarkerOverlayClasses } from './CustomMarkerOverlay';
import { cn } from '@/components/ui/cn';
import { GoogleMapsRoutingResponse } from '@/modules/map/engine/GoogleTypes';
import { X4bActivityStatus } from '@/services/signalr.service';
import { TripData } from '../engine/ConciergeCardViewModel';
import { debugLog } from '@/common/DebugHelper';
import { BookingType, GetGoogleMapRoutingResponse, GetGoogleTokenResponse } from '@/modules/vehicle/engine/ActivityTypes';
import { GenericResponse } from '@/common/common_utils';

//export const routeColor1 = '#004060';
export const mainRouteColor = '#004060';
export const remainingRouteColor = '#0A84FF';
export const routeStrokeWidth = 6;


export interface LiveMapProps {
	bindOrderUuidSetter?: (setter: (orderUuid: string) => void) => void;
	bindTripIdSetter?: (setter: (tripId: string) => void) => void;
	//shouldShowRoute?: boolean;
	pickupLocationName?: string;
	dropOffLocationName?: string;
	mapDivClassName?: string;
	initialRoute?: GoogleMapsRoutingResponse | null;
	activity?: X4bActivityDto;
	markersAsDots?: boolean;
	showCompletedRoute?: boolean;
	// selectGeoZone: {
	// 	PolygonGeometries?: Array<{ Lat: number; Lng: number }>;
	// };
	// onMapBoundsChanged?: (bounds: {
	// 	NorthWestPoint: { lat: number; long: number };
	// 	SouthEastPoint: { lat: number; long: number };
	// }) => void;
}

/**
 * Fetches route data for an activity - either initial route or Google Fleet data
 * depending on the activity status.
 *
 * @param activity - The activity to fetch route data for
 * @param activityId - The ID of the activity
 * @param xpressOrderId - The XpressOrderId of the activity
 * @param setRoutingData - Callback to set the routing data
 * @param orderUuidSetterRef - Ref to the order UUID setter function
 * @param tripIdSetterRef - Ref to the trip ID setter function
 */
export const fetchActivityRouteData = async (
	activity: X4bActivityDto,
	activityId: number,
	xpressOrderId: string,
	setRoutingData: (data: GoogleMapsRoutingResponse | null) => void,
	orderUuidSetterRef: React.MutableRefObject<((orderUuid: string) => void) | undefined>,
	tripIdSetterRef: React.MutableRefObject<((tripId: string) => void) | undefined>,
	showCompletedRoute?: boolean
): Promise<void> => {
	// Reset routing data when activity changes
	setRoutingData(null);

	// For requesting, canceled, or scheduled trips, fetch the initial route
	// When showCompletedRoute is set, completed trips also use the initial route
	if (ActivityHelper.isRequesting(activity) || ActivityHelper.isCanceled(activity) || ActivityHelper.isScheduled(activity)
		|| (showCompletedRoute && ActivityHelper.isCompleted(activity))) {
		const fetchInitialRoute = async () => {
			try {
				const response = await axiosInstance.post<GenericResponse<GetGoogleMapRoutingResponse>>(
					ENDPOINTS['/api/X4bActivity/GetGoogleMapRouting'],
					{ Id: activityId }
				);

				// Parse the JSON string to get the routing object
				if (response.data.Data.GoogleMapRouting) {
					const parsedRoute = JSON.parse(response.data.Data.GoogleMapRouting) as GoogleMapsRoutingResponse;
					setRoutingData(parsedRoute);
				}
			} catch (error) {
				console.error('Failed to fetch initial route:', error);
			}
		};
		fetchInitialRoute();
	} else {
		// For active trips, use Google Fleet Engine
		orderUuidSetterRef.current?.(xpressOrderId);

		const fetchTripId = async () => {
			try {
				const token = await axiosInstance.get<GetGoogleTokenResponse>(
					ENDPOINTS['/api/X4bActivity/GetGoogleFleetToken'],
					{ params: { XpressOrderId: xpressOrderId } }
				);
				tripIdSetterRef.current?.(token.data.GoogleTripId);
			} catch (error) {
				console.error('Failed to fetch Google Fleet token:', error);
			}
		};

		fetchTripId();
	}
};

// Decode Google Maps polyline string to array of LatLng coordinates
const decodePolyline = (encoded: string): google.maps.LatLng[] => {
	const poly: google.maps.LatLng[] = [];
	let index = 0;
	const len = encoded.length;
	let lat = 0;
	let lng = 0;

	while (index < len) {
		let b: number;
		let shift = 0;
		let result = 0;

		// Decode latitude
		do {
			b = encoded.charCodeAt(index++) - 63;
			result |= (b & 0x1f) << shift;
			shift += 5;
		} while (b >= 0x20);

		const dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
		lat += dlat;

		shift = 0;
		result = 0;

		// Decode longitude
		do {
			b = encoded.charCodeAt(index++) - 63;
			result |= (b & 0x1f) << shift;
			shift += 5;
		} while (b >= 0x20);

		const dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
		lng += dlng;

		const point = new google.maps.LatLng(lat / 1e5, lng / 1e5);
		poly.push(point);
	}

	return poly;
};

// Drawing of initial route is mostly independant of drawing of the route by google fleet when ride is in progress.
export function useInitialRoute(props: LiveMapProps, customPickupOverlayRef: React.RefObject<any>, customDropoffOverlayRef: React.RefObject<any>, setMap: React.Dispatch<React.SetStateAction<google.maps.Map | undefined>>, map?: google.maps.Map) {
	const initialRoutePolylineRef = useRef<google.maps.Polyline | null>(null);
	//const [map, setMap] = useState<google.maps.Map | null>(null);

	// Effect to render Google Maps preview route
	useEffect(() => {
		// Avoid rendering preview route if we have routing data, a map, and no active trip
		if (!props.initialRoute
			|| !map
			|| (props.activity?.Status
				&& (props.activity?.Status >= X4bActivityStatus.RiderAssigned
					&& props.activity?.Status != X4bActivityStatus.Canceled
					&& !(props.showCompletedRoute && props.activity?.Status == X4bActivityStatus.Completed)
					&& !((props.activity?.Status == X4bActivityStatus.New || props.activity?.Status == X4bActivityStatus.RiderAccept)
						&& props.activity.BookingType == BookingType.Scheduled)))) {
			// Remove preview route if conditions are not met
			if (initialRoutePolylineRef.current) {
				debugLog(`[LiveMap] PATH UPDATE: Removing initial route polyline (no initial route or map exists or trip is active)`);
				initialRoutePolylineRef.current.setMap(null);
				initialRoutePolylineRef.current = null;
			}
			// Hide custom overlays when preview route is not shown
			if (customPickupOverlayRef.current) {
				debugLog(`[LiveMap] MARKER UPDATE: Hiding pickup marker for preview route`);
				customPickupOverlayRef.current.setMap(null);
			}
			if (customDropoffOverlayRef.current) {
				debugLog(`[LiveMap] MARKER UPDATE: Hiding dropoff marker for preview route`);
				customDropoffOverlayRef.current.setMap(null);
			}
			return;
		}

		try {
			const route = props.initialRoute.routes?.[0];
			if (!route || !route.legs || route.legs.length === 0) {
				return;
			}

			// Collect all coordinates from all legs and steps
			const allCoordinates: google.maps.LatLng[] = [];

			for (const leg of route.legs) {
				if (!leg.steps || leg.steps.length === 0) continue;

				for (const step of leg.steps) {
					if (step.polyline?.points) {
						// Decode the polyline and add coordinates
						const decodedPoints = decodePolyline(step.polyline.points);
						allCoordinates.push(...decodedPoints);
					}
				}
			}

			if (allCoordinates.length === 0) {
				return;
			}

			// Remove existing preview route if it exists
			if (initialRoutePolylineRef.current) {
				debugLog(`[LiveMap] PATH UPDATE: Removing existing initial route polyline before creating new one`);
				initialRoutePolylineRef.current.setMap(null);
			}

			// Create new polyline for preview route
			debugLog(`[LiveMap] PATH UPDATE: Creating new initial route polyline with ${allCoordinates.length} coordinates`);
			const polyline = new google.maps.Polyline({
				path: allCoordinates,
				strokeColor: mainRouteColor,
				//strokeOpacity: 0.8, // Slightly transparent to not obstruct labels
				strokeWeight: 5,
				zIndex: 1, // Very low z-index to render beneath all other elements including labels
				map: map,
				visible: !props.activity || props.activity.Status === X4bActivityStatus.Canceled
				|| (props.showCompletedRoute && props.activity.Status === X4bActivityStatus.Completed)
			});

			initialRoutePolylineRef.current = polyline;

			// Fit map bounds to show the entire route
			if (route.bounds) {
				const bounds = new google.maps.LatLngBounds(
					new google.maps.LatLng(route.bounds.southwest.lat, route.bounds.southwest.lng),
					new google.maps.LatLng(route.bounds.northeast.lat, route.bounds.northeast.lng)
				);
				map.fitBounds(bounds);
			}

			// Create custom overlays for pickup and dropoff locations
			// Check if we should show completed markers (dots) for completed or canceled trips
			const isActivityCompleted = props.activity ? ActivityHelper.isCompleted(props.activity) : false;
			const isActivityCanceled = props.activity ? ActivityHelper.isCanceled(props.activity) : false;
			const shouldShowCompletedMarkers = (isActivityCompleted && !props.showCompletedRoute) || isActivityCanceled || props.markersAsDots;

			const { CustomMarkerOverlay, CompletedMarkerOverlay } = createMarkerOverlayClasses();

			// Remove existing pickup overlay if it exists
			if (customPickupOverlayRef.current) {
				debugLog(`[LiveMap] MARKER UPDATE: Removing existing pickup marker for preview route`);
				customPickupOverlayRef.current.setMap(null);
				customPickupOverlayRef.current = null;
			}

			// Get start location (pickup) from the first leg
			const firstLeg = route.legs[0];
			if (firstLeg.start_location) {
				const position = new google.maps.LatLng(
					firstLeg.start_location.lat,
					firstLeg.start_location.lng
				);

				if (shouldShowCompletedMarkers) {
					debugLog(`[LiveMap] MARKER UPDATE: Creating completed pickup marker for preview route at (${firstLeg.start_location.lat}, ${firstLeg.start_location.lng})`);
					const overlay = new CompletedMarkerOverlay(
						position,
						"/images/concierge/live-map/pickup-completed-location.svg",
						"Pick up",
						props.pickupLocationName
					);
					overlay.setMap(map);
					customPickupOverlayRef.current = overlay;
				} else {
					debugLog(`[LiveMap] MARKER UPDATE: Creating pickup marker for preview route at (${firstLeg.start_location.lat}, ${firstLeg.start_location.lng})`);
					const overlay = new CustomMarkerOverlay(
						position,
						"/images/concierge/live-map/pickup-location.svg",
						"Pick up",
						props.pickupLocationName
					);
					overlay.setMap(map);
					customPickupOverlayRef.current = overlay;
				}
			}

			// Remove existing dropoff overlay if it exists
			if (customDropoffOverlayRef.current) {
				debugLog(`[LiveMap] MARKER UPDATE: Removing existing dropoff marker for preview route`);
				customDropoffOverlayRef.current.setMap(null);
				customDropoffOverlayRef.current = null;
			}

			// Get end location (dropoff) from the last leg
			const lastLeg = route.legs[route.legs.length - 1];
			if (lastLeg.end_location) {
				const position = new google.maps.LatLng(
					lastLeg.end_location.lat,
					lastLeg.end_location.lng
				);

				if (shouldShowCompletedMarkers) {
					debugLog(`[LiveMap] MARKER UPDATE: Creating completed dropoff marker for preview route at (${lastLeg.end_location.lat}, ${lastLeg.end_location.lng})`);
					const overlay = new CompletedMarkerOverlay(
						position,
						"/images/concierge/live-map/dropoff-completed-location.svg",
						"Drop off",
						props.dropOffLocationName
					);
					overlay.setMap(map);
					customDropoffOverlayRef.current = overlay;
				} else {
					debugLog(`[LiveMap] MARKER UPDATE: Creating dropoff marker for preview route at (${lastLeg.end_location.lat}, ${lastLeg.end_location.lng})`);
					const overlay = new CustomMarkerOverlay(
						position,
						"/images/concierge/live-map/dropoff-location.svg",
						"Drop off",
						props.dropOffLocationName
					);
					overlay.setMap(map);
					customDropoffOverlayRef.current = overlay;
				}
			}
		} catch (error) {
			console.error('Failed to render Google Maps preview route:', error);
		}
		return () => {
			// Clean up preview route polyline
			if (initialRoutePolylineRef.current) {
				initialRoutePolylineRef.current.setMap(null);
				initialRoutePolylineRef.current = null;
			}
		}
	}, [props.initialRoute, map, props.activity?.Status, props.pickupLocationName, props.dropOffLocationName, props.markersAsDots]);
}
